import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  Input,
  OnInit,
} from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import {
  PatientTargetDataModel,
  PatientTargetModel,
  PatientTargetStatus,
  PromptCodeModel,
  PromptScheduleModel,
} from '../../../core/api/models';
import {
  getPatientTargetStatusColor,
  getPatientTargetStatusLabel,
} from '../../../shared/utils/utils';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
@Component({
  selector: 'app-box-dtt',
  templateUrl: './box-dtt.component.html',
  styleUrls: ['./box-dtt.component.css', '../data-entry-feedback.css'],
  standalone: false,
})
export class BoxDttComponent extends BaseBoxComponent implements OnInit {
  text: string | undefined = '-';
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  targetID: number = 0;
  requiredTrials: number = 0;
  targetStatus: PatientTargetStatus = PatientTargetStatus.Ready;
  @Input() enabled?: boolean = true;
  currentPromptCodeID?: number;
  initialPromptCodeID?: number;
  @Input() promptSchedule?: PromptScheduleModel | null;

  targetData: PatientTargetDataModel[] = [];
  totalSuccess: number = 0;
  selectedOptions: (PromptCodeModel & {
    RecordedDate?: string;
    FormattedDate?: string;
  })[] = [];

  @Input() options: PromptCodeModel[] = [];
  @Input() allPromptCodes: PromptCodeModel[] = [];
  selectedOption: PromptCodeModel | null = null;

  @Input() target: PatientTargetModel | undefined = undefined;
  remainingTrials: number = 0;

  currentPromptCodeChangedDate: string | null = null;

  @Input() isPreview?: boolean;

  addingTargetData: boolean = false;
  /** Option ID that was just recorded (success flash); cleared after 220ms. */
  lastRecordedOptionId: number | null = null;
  /** Option ID showing cooldown bar; cleared when buffer ends. */
  cooldownOptionId: number | null = null;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(
        this.targetID
      );
      if (targetData) {
        this.targetData = targetData.filter((t) => !t.DeletedDate);

        this.remainingTrials =
          (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (
          this.target?.TargetStatus === PatientTargetStatus.Mastered ||
          this.target?.TargetStatus === PatientTargetStatus.Closed ||
          this.target?.TargetStatus === PatientTargetStatus.Fluent
        ) {
          this.remainingTrials = 0;
        }

        this.calculateCurrentPromptCodeID();
        //Add the options inside targetData to the selectedOptions based on the options available in the options array
        this.selectedOptions = this.targetData
          .map((t) => {
            let option: PromptCodeModel & {
              RecordedDate: string;
              FormattedDate: string;
            } = this.allPromptCodes.find(
              (o) => o.ID === t.IntValue
            ) as PromptCodeModel & {
              RecordedDate: string;
              FormattedDate: string;
            };
            option.RecordedDate = t.RecordedDate ?? '';
            option.FormattedDate = this.formatDate(
              new Date(t.RecordedDate ?? '')
            );
            return option;
          })
          .filter((o) => o !== undefined) as (PromptCodeModel & {
          RecordedDate: string;
          FormattedDate: string;
        })[];

        this.totalSuccess = this.selectedOptions.filter(
          (o) => o.IsIndependent
        ).length;
        if (this.selectedOptions.length > 0) {
          this.selectedOption =
            this.selectedOptions[this.selectedOptions.length - 1];
        } else {
          this.selectedOption = null;
        }
      } else {
        this.targetData = [];
        this.selectedOptions = [];
        this.totalSuccess = 0;
        this.selectedOption = null;
      }
    });
  }

  ngOnInit(): void {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    this.targetStatus = this.target?.TargetStatus ?? PatientTargetStatus.Ready;
    this.requiredTrials = this.target?.DesiredTrials ?? 0;

    this.currentPromptCodeID = this.target?.CurrentPromptCodeID ?? 0;
    if (!this.currentPromptCodeID) {
      if (this.promptSchedule?.IsInverse) {
        this.currentPromptCodeID =
          this.options[this.options.length - 1].ID ?? 0;
      } else {
        this.currentPromptCodeID = this.options[0].ID ?? 0;
      }
    }

    this.currentPromptCodeChangedDate =
      this.target?.CurrentPromptCodeChangedDate ?? null;

    if (!this.initialPromptCodeID || this.initialPromptCodeID === 0) {
      this.initialPromptCodeID = this.currentPromptCodeID;
    }
  }

  calculateCurrentPromptCodeID() {
    // Get all a count of the promptcodes recorded on targetdata for this target since the last currentPromptCodeChangedDate
    const allRecordsSinceLastChanged = this.targetData
      .filter(
        (x) =>
          x.RecordedDate &&
          (this.currentPromptCodeChangedDate === null ||
            new Date(x.RecordedDate) >
              new Date(this.currentPromptCodeChangedDate ?? ''))
      )
      .sort((a, b) => {
        // OrderBy equivalent
        if (
          a.RecordedDate &&
          b.RecordedDate &&
          new Date(a.RecordedDate) < new Date(b.RecordedDate)
        )
          return -1;
        if (
          a.RecordedDate &&
          b.RecordedDate &&
          new Date(a.RecordedDate) > new Date(b.RecordedDate)
        )
          return 1;
        return 0;
      });

    let countPromotion = 0;
    let countPromotionHigher = 0;
    let countDemotion = 0;
    let countDemotionLower = 0;

    // Get order of the current promptcode
    const currentPromptCode = this.options.find(
      (x) => x.ID === this.currentPromptCodeID
    );
    let currentPromptCodeOrder = -1;

    if (currentPromptCode && currentPromptCode.Order) {
      currentPromptCodeOrder = currentPromptCode.Order;
    } else {
      //If the promptschedule is set to inverse, we need to start from the last promptcode
      if (this.promptSchedule?.IsInverse) {
        currentPromptCodeOrder = this.options[this.options.length - 1].Order!;
      } else {
        if (this.options && this.options.length > 0 && this.options[0].Order) {
          currentPromptCodeOrder = this.options[0].Order;
        }
      }
    }

    let finalNewPromptCode: PromptCodeModel | null = null;
    let finalNewPromptCodeDate: string | null = null;
    for (const record of allRecordsSinceLastChanged) {
      const promptCode = this.options.find((x) => x.ID === record.IntValue);
      if (!promptCode) {
        continue;
      }

      let shouldPromote = false;

      countPromotion++;

      if (promptCode!.Order! >= currentPromptCodeOrder) {
        countPromotionHigher++;
      }

      if (this.promptSchedule?.PromotionTotal === countPromotion) {
        if (countPromotionHigher >= this.promptSchedule?.PromotionNeeded!) {
          shouldPromote = true;
          countPromotion = 0;
          countPromotionHigher = 0;
        }
      }

      let shouldDemote = false;
      countDemotion++;

      if (promptCode!.Order! < currentPromptCodeOrder) {
        countDemotionLower++;
      }

      if (this.promptSchedule?.DemotionTotal === countDemotion) {
        if (countDemotionLower >= (this.promptSchedule?.DemotionNeeded ?? 0)) {
          shouldDemote = true;
          countDemotion = 0;
          countDemotionLower = 0;
        }
      }

      if (shouldPromote && shouldDemote) {
        if (this.promptSchedule?.TieIsDemotion) {
          shouldPromote = false;
        } else {
          shouldDemote = false;
        }
      }

      if (shouldPromote) {
        const newPromptCode = this.options.find(
          (x) => x.Order! > currentPromptCodeOrder && !x.Inactive
        );

        if (newPromptCode) {
          finalNewPromptCode = newPromptCode;
          finalNewPromptCodeDate = record.RecordedDate ?? null;
        }

        countPromotion = 0;
        countPromotionHigher = 0;
        countDemotion = 0;
        countDemotionLower = 0;
      }

      if (shouldDemote) {
        const newPromptCode = this.options
          .filter((x) => x.Order! < currentPromptCodeOrder && !x.Inactive)
          .sort((a, b) => b.Order! - a.Order!)[0]; // OrderByDescending equivalent

        if (newPromptCode) {
          finalNewPromptCode = newPromptCode;
          finalNewPromptCodeDate = record.RecordedDate ?? null;
        }

        countPromotion = 0;
        countPromotionHigher = 0;
        countDemotion = 0;
        countDemotionLower = 0;
      }
    }
    if (finalNewPromptCode) {
      this.currentPromptCodeID = finalNewPromptCode.ID!;
      this.currentPromptCodeChangedDate = finalNewPromptCodeDate;
    }
  }

  selectOption(option: PromptCodeModel) {
    // Store the selected option
    this.selectedOption = option;

    // Initialize the selectedOptions array if it doesn't exist
    if (!this.selectedOptions) {
      this.selectedOptions = [];
    }

    this.addSessionValue(option);
  }

  addSessionValue(option: PromptCodeModel): void {
    if (this.addingTargetData) {
      return;
    }
    this.addingTargetData = true;
    //get date with up to seconds, remove milisencods
    const recordedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');
    const dateToSave = new Date(recordedDate).fromUTCtoServerString();

    //check if the dateToSave is already in the targetData
    const isDateInTargetData = this.targetData.some(
      (t) => t.RecordedDate === dateToSave
    );
    if (isDateInTargetData) {
      this.addingTargetData = false;
      return;
    }

    this.patientSessionService.addTargetData({
      IntValue: option.ID,
      TextValue: option.Text,
      PatientTargetID: this.targetID,
      PatientSessionID: 0,
      RecordedDate: dateToSave,
    });
    this.lastRecordedOptionId = option.ID ?? null;
    this.cooldownOptionId = option.ID ?? null;
    setTimeout(() => {
      this.lastRecordedOptionId = null;
    }, 220);
    setTimeout(() => {
      this.addingTargetData = false;
      this.cooldownOptionId = null;
    }, 1000);
  }

  getIcon(option: PromptCodeModel): string {
    //get first letter of the text
    return option.Abbreviation ?? '';
  }

  getStatusText() {
    if (!this.enabled) {
      return 'Waiting';
    }
    return getPatientTargetStatusLabel(this.targetStatus);
  }

  getStatusColor() {
    if (!this.enabled) {
      return '#CCCCCC';
    }
    return getPatientTargetStatusColor(this.targetStatus);
  }

  override doLongPress(): void {
    this.showExtra2();
  }

  clickedBox() {
    if (this.extra2Opened) {
      return;
    }
    this.showExtra();
  }

  openExtra2(event: Event) {
    event.stopPropagation();
    this.showExtra2();
  }

  deleteData(date?: string) {
    if (date) {
      //confirm the deletion
      const confirmed = this.alertService.confirm(
        'Are you sure you want to delete this data?',
        'Delete Data'
      );
      confirmed.subscribe((result) => {
        if (result) {
          let targetDataToSave: PatientTargetDataModel | undefined =
            this.targetData.find((t) => t.RecordedDate === date);

          if (targetDataToSave) {
            targetDataToSave.DeletedDate = new Date().toServerString();
            this.patientSessionService.saveTargetData(targetDataToSave);
          }
        }
      });
    }
  }
}
